import './PageContainer.scoped.scss'
import useAct from '../useAct'
import EditableName from 'Shared/components/EditableName/EditableName'
import { ManageMenuItem } from 'Shared/components/ManageMenu';
import ManageMenuComponent from 'Shared/components/ManageMenu/ManageMenuComponent'
import CopyToClipboard from 'Shared/components/CopyToClipboard'
import ExportConfirmationModal from 'Shared/components/ExportConfirmationModal';
import { ConfirmationModal } from 'Shared/components/ConfirmationModal';
import BackdropModal from 'Shared/components/BackdropModal/BackdropModal';
import FloatingMenu from 'Shared/components/FloatingMenu/FloatingMenu';
import RefreshTargets from 'Shared/components/RefreshTargets';
import OverviewStats from './OverviewStats';
import QuestionStats, { useOrderedStatsQuestions } from 'Shared/Surveys/components/QuestionStats';
import FieldStats from './FieldStats';

export default function PageContainer({ dashboardId, dashboardName }) {
  const act = useAct()
  const dashboard = useSel((s) => s.dashboard)
  const orderedStatsQuestions = useOrderedStatsQuestions()

  useEffect(() => {
    if (dashboardId) {
      act.dashboard.load(dashboardId)
      act.dashboard.loadStats(dashboardId)
      act.dashboard.loadQuestionsAndAnswers(dashboardId)
    }
  }, [dashboardId])

  const [exportPassword, setExportPassword] = useState(null)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [refreshTargetsModalOpen, setRefreshTargetsModalOpen] = useState(false)
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
  const [duplicateDashboardName, setDuplicateDashboardName] = useState('')
  const [duplicateDashboardAttachSameAudience, setDuplicateDashboardAttachSameAudience] = useState(false)
  const [duplicateModalError, setDuplicateModalError] = useState(null)

  const doExport = () => {
    act.dashboard.exportDashboard().then((response) => {
      setExportPassword(response.data.password)
    })
  }

  const handleCloseDashboard = () => {
    act.dashboard.closeDashboard().then(() => {
      window.location.reload();
    })
  }

  const handleArchiveDashboard = () => {
    act.dashboard.archiveDashboard().then(() => {
      window.location.href = '/admin/dashboards';
    })
  }

  const handleCloneDashboard = () => {
    setDuplicateModalError(null)
    const newName = duplicateDashboardName.trim()
    if (!newName) return

    act.dashboard.cloneDashboard({
      name: newName,
      useSameSearch: duplicateDashboardAttachSameAudience,
    }).then((response) => {
      setDuplicateModalOpen(false)
      const { redirectUrl } = response.data
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    }).catch((error) => {
      setDuplicateModalError(error.response?.data?.error || 'An error occurred while duplicating the dashboard.')
    })
  }

  const menuItems: ManageMenuItem[] = useMemo(() => {
    const baseItems = [
      {
        title: (
          <span className="flex items-center">Edit</span>
        ),
        onClick: () => {
          window.location.href = `/admin/dashboards/${dashboardId}/build`;
        },
      },
      {
        title: (
          <span className="flex items-center">Export results</span>
        ),
        onClick: () => doExport('default'),
      },
      {
        title: (
          <span className="flex items-center">Duplicate dashboard</span>
        ),
        onClick: () => {
          setDuplicateDashboardName(dashboard?.name || '')
          setDuplicateDashboardAttachSameAudience(false)
          setDuplicateModalOpen(true)
        },
      },
      {
        title: <span className="flex items-center">Refresh audience</span>,
        onClick: () => setRefreshTargetsModalOpen(true)
      }
    ];

    if (dashboard?.status !== 'closed') {
      baseItems.push({
        title: (
          <span className="flex items-center danger-red">Close dashboard</span>
        ),
        onClick: () => setShowCloseModal(true),
      });
    } else {
      baseItems.push({
        title: (
          <span className="flex items-center">Re-open dashboard</span>
        ),
        onClick: () => act.dashboard.reopenDashboard(),
      });
      baseItems.push({
        title: (
          <span className="flex items-center danger-red">Archive dashboard</span>
        ),
        onClick: () => setShowArchiveModal(true),
      });
    }

    return baseItems;
  }, [dashboardId, dashboard?.status]);

  const sidebarNavItems = useMemo(() => {
    let items = [
      { name: 'Status', tag: 'status' },
    ]
    if (dashboard?.stats?.fieldStats?.length > 0) {
      items.push({ name: 'Usage', tag: 'usage-stats' })
    }
    if (orderedStatsQuestions?.length > 0) {
      items.push({ name: 'Questions', tag: 'question-stats' })
    }
    return items
  }, [dashboard, orderedStatsQuestions])

  const refreshTargetPopup = useMemo(() => {
    if (!refreshTargetsModalOpen) { return null; }

    return <RefreshTargets
      onComplete={() => setRefreshTargetsModalOpen(false)}
      onClose={() => setRefreshTargetsModalOpen(false)}
      entityName="dashboard"
      refreshTargetFunction={(dryRun) => act.dashboard.refreshTargets({ dryRun })}
      onCommit={() => act.dashboard.loadStats(dashboardId)}
      />
  }, [refreshTargetsModalOpen, dashboardId]);

  return (
  <>

    <div className="header dashboard">
      <a className='back' href="/admin/dashboards"></a>
      <div className="name-container">
        <EditableName
          modelName="dashboard"
          endpoint={`/api/dashboards/${dashboardId}`}
          noMarginBottom={true}
        />
        <div className="actions">
          <ManageMenuComponent
            items={menuItems}
            components={[]}
            buttonType='secondary'
          />
          {dashboard?.status !== 'draft' && <a className='button primary' target='_blank' href={`/dashboards/${dashboard?.id}`} rel="noreferrer">View</a>}
        </div>

    <div className='url'>
     <span>{dashboard?.publicUrl}</span>
     <div className='copier'><CopyToClipboard text="Copy" url={dashboard?.publicUrl} /></div>
    </div>

      </div>
    </div>

    <div className='dashboard-overview margin-top double'>
      <div className="left">
        <FloatingMenu items={sidebarNavItems} />
      </div>
      <div className='right'>
        <OverviewStats />
        {dashboard?.stats?.fieldStats?.length > 0 && <FieldStats />}
        {orderedStatsQuestions?.length > 0 &&
        <div className='box margin-top double no-side-padding' id='question-stats'>
            <h2 className='question-stats-header'>Questions</h2>
            <div className="question-stats-container">
              <QuestionStats />
            </div>
        </div>}
      </div>
    </div>

    <ExportConfirmationModal
      exportPassword={exportPassword}
      setExportPassword={setExportPassword}
      entityName="dashboard" />

    <ConfirmationModal
      open={showCloseModal}
      hide={() => setShowCloseModal(false)}
      onSubmit={handleCloseDashboard}
      confirmValue="close"
    />

    <ConfirmationModal
      open={showArchiveModal}
      hide={() => setShowArchiveModal(false)}
      onSubmit={handleArchiveDashboard}
      confirmValue="delete"
    />

    <BackdropModal
      title="Duplicate Dashboard"
      open={duplicateModalOpen}
      onCancel={() => setDuplicateModalOpen(false)}
    >
      <div className="wizard">
        <div className="step">
          <div className='form narrow'>
            <div className='fields'>
              <div className='field'>
                <label>New dashboard name</label>
                <input
                  type='text'
                  placeholder='Name your dashboard'
                  value={duplicateDashboardName}
                  onChange={(e) => setDuplicateDashboardName(e.target.value)}
                />
              </div>
            </div>
            {dashboard?.searchId && (
              <div className='field margin-top half'>
                <label className='align-center'>
                  <input
                    type="checkbox"
                    checked={duplicateDashboardAttachSameAudience}
                    onChange={(e) => setDuplicateDashboardAttachSameAudience(e.target.checked)}
                  />
                  <p className='no-margin-top no-margin-bottom margin-left'>Attach the same audience to the duplicated dashboard</p>
                </label>
              </div>
            )}
            <button
              className='button block primary no-margin-left'
              onClick={handleCloneDashboard}
            >Duplicate</button>
            {duplicateModalError && <p className='error margin-top'>{duplicateModalError}</p>}
          </div>
        </div>
      </div>
    </BackdropModal>

    {refreshTargetPopup}
    </>
  )
}
